import { Component, computed, effect, inject } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';

import { RouteExtras } from '../../interfaces/route-extras.interface';
import { OrderStore } from '../../order-store';
import { ApiService } from '../../services/api.service';

interface Slot {
  from: string;
  to: string;
}

@Component({
  selector: 'app-time-form',
  imports: [ReactiveFormsModule, MatFormFieldModule, MatSelectModule, DatePipe],
  templateUrl: './time-form.html',
  styleUrl: './time-form.scss',
})
export class TimeForm {
  private readonly store = inject(OrderStore);
  private readonly formBuilder = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly apiService = inject(ApiService);

  private readonly routeExtras: RouteExtras =
    (this.router.getCurrentNavigation()?.extras.state as RouteExtras) ?? {};

  readonly formGroup = this.formBuilder.group({
    date: ['', Validators.required],
    time: ['', Validators.required],
  });

  readonly groupedSlots = computed<Record<string, Slot[]>>(() => {
    const slots = this.store.timeslots()?.slots ?? [];
    const result: Record<string, Slot[]> = {};
    const datePipe = new DatePipe('en-US');
    for (const item of slots) {
      const key = datePipe.transform(item.from, 'yyyy-MM-dd') ?? '';
      (result[key] ||= []).push(item);
    }
    return result;
  });

  readonly isLoaded = computed(
    () => Object.keys(this.groupedSlots()).length > 0,
  );
  readonly days = computed(() => Object.keys(this.groupedSlots()));

  readonly allowPreferredDateConfirmation = computed(
    () => this.store.order()?.allowPreferredDateConfirmation,
  );

  constructor() {
    effect(() => {
      if (this.store.timeslots() && !this.isLoaded()) {
        this.navigate();
      }
    });
  }

  public save(): void {
    const value = this.formGroup.getRawValue();
    const slots = this.groupedSlots()[value.date!];
    this.store.setConfirmedData({
      time: slots[Number(value.time)].from as unknown as number,
    });
    if (this.routeExtras.submit) {
      this.apiService.confirmData();
    }
    this.navigate();
  }

  private navigate(): void {
    this.router.navigate(['main'], { queryParamsHandling: 'merge' });
  }
}
